/**
 * main.ts — orchestration: rAF render loop + agent timer + painting + run
 * lifecycle.
 *
 * Structure: the rAF loop runs for the lifetime of the page (cheap; it just
 * eases the agent and blits layers). The agent ticks on a separate
 * setInterval so the agent speed (Hz) is decoupled from the render rate.
 *
 * Run lifecycle:
 *   - idle    — user paints the world, picks hyperparams. The renderer still
 *               draws the world + belief (the belief is empty until the agent
 *               runs).
 *   - running — env has been reset; the agent ticks each interval. When
 *               env.done fires, an FQI round may run (if enough new
 *               transitions accumulated) and the next episode starts on a
 *               FRESHLY RANDOMISED layout (the agent must generalise, not
 *               memorise a fixed map).
 *   - stopped — timer cleared; the last frame stays up. Reset to go again.
 *
 * Learning happens across EPISODES: each episode's layout randomises
 * (spiral-with-jittered-centre or scatter-with-randomised-stone-density).
 * FQI refits Q on the accumulated replay buffer — one closed-form ridge
 * solve per round — and across rounds the agent gets better at collecting
 * gold from ANY layout.
 */

import './style.css';
import { World, Env, DIRECTIONS, decodeAction } from './environment';
import { Agent } from './agent';
import { Renderer, makeAgentAnim, type AgentAnim } from './render';
import {
  SidePanel,
  type Brush,
  generateSpiral,
  generateRandom,
  generateClustered,
  generateEmpty,
  randomizeWorld,
  DEFAULT_TIMEOUT_MS,
} from './ui';
import { HelpModal, ESSAY_URL } from './help';
import { FREE } from './belief';
import { makeRng } from './linalg';
import { epsilonForRound } from './fqi';
import {
  type ModelKind,
  type WorldType,
  type VariantSpec,
  specFor,
  parseModelParam,
  parseWorldParam,
} from './variant';

const GRID = 30; // 30×30 grid — ~15px cells on a 450px canvas
const START_R = Math.floor(GRID / 2);
const START_C = Math.floor(GRID / 2);
/** Default agent tick rate. 30 Hz — fast enough to accumulate ~25 episodes
 *  per minute (the per-round collection target), slow enough for the viewer
 *  to follow individual moves. The slider lets the user slow down to watch. */
const DEFAULT_SPEED_HZ = 30;
/** Per-episode step cap. Short episodes = frequent resets = diverse start
 *  states for the replay buffer (this is how the validated prototype trained). */
const EPISODE_STEP_CAP = 400;
/** How many new episodes between forced fit rounds. Lower = Q updates more
 *  often (more visible learning); higher = each fit sees more data (more
 *  stable). 3 is a good demo-time balance. */
const EPISODES_PER_FIT_ROUND = 3;

function main(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = '';

  // Layout: canvas (left) + side panel (right).
  const lakeCanvas = document.createElement('canvas');
  lakeCanvas.className = 'lake';
  const lakeWrap = document.createElement('div');
  lakeWrap.className = 'lake-wrap';
  lakeWrap.appendChild(lakeCanvas);
  app.appendChild(lakeWrap);

  const panelSlot = document.createElement('div');
  panelSlot.className = 'panel-slot';
  app.appendChild(panelSlot);

  // ── Persistent help button (top-left) ───────────────────────────────
  // Mounted to document.body so it floats above every overlay, exactly like
  // the fishing-demo's pattern.
  const helpBtn = document.createElement('button');
  helpBtn.className = 'help-btn';
  helpBtn.type = 'button';
  helpBtn.textContent = '?';
  helpBtn.setAttribute('aria-label', 'What is this?');
  document.body.appendChild(helpBtn);
  let helpModal: HelpModal | null = null;
  function showHelp(): void {
    if (helpModal) return;
    helpModal = new HelpModal(() => {
      helpModal?.remove();
      helpModal = null;
    });
    helpModal.mount(document.body);
  }
  helpBtn.addEventListener('click', showHelp);

  // ── Persistent footer (bottom-center, always visible) ────────────────
  // Mounted to document.body so it floats above every layer, exactly like
  // the fishing-demo's footer. Carries the author byline + essay link.
  const footer = document.createElement('footer');
  footer.className = 'app-footer';
  footer.innerHTML = `Mixture of Bandits © Anton A. Nesterov — <a href="${ESSAY_URL}" target="_blank" rel="noopener">Read Essay</a>`;
  document.body.appendChild(footer);

  // ── Core objects ────────────────────────────────────────────────────
  const world = new World(GRID);
  generateSpiral(world);

  // PRNG used both for painting scatter layouts AND for the per-episode
  // randomisation. Distinct from the env's sensor RNG.
  const worldRng = makeRng(1234);
  const env = new Env(world, makeRng(99));
  const renderer = new Renderer(lakeCanvas);

  // Agent + agent animation state.
  // The variant is chosen from the ?model= URL param at load time and can
  // be swapped at runtime via the sidebar toggle (which calls onSelectModel
  // and rebuilds the agent from scratch).
  const params = new URLSearchParams(window.location.search);
  let modelKind: ModelKind = parseModelParam(params.get('model'));
  let worldType: WorldType = parseWorldParam(params.get('world'));
  let spec: VariantSpec = specFor(modelKind);
  let agent = new Agent({ seed: 0, spec });
  let agentAnim: AgentAnim = makeAgentAnim(START_R, START_C);

  // Mutable UI/hyperparam state.
  let brush: Brush = 'gold';
  let gamma = 0.95;
  let lambda = 1.0;
  let epsMax = 0.9;
  let epsMin = 0.15;
  let speedHz = DEFAULT_SPEED_HZ;
  let sensorP = 0.85;
  // Reveal-all defaults ON (the user asked). The belief heat-map still shows
  // what the agent has sensed, so the demo can show both views at once.
  let revealAll = true;
  let showBelief = true;
  let timeoutMs = DEFAULT_TIMEOUT_MS;
  // Custom ε-schedule overrides (when the user moves the sliders).
  let epsScheduleOverride: ((round: number) => number) | null = null;

  // Run state.
  type Phase = 'idle' | 'running' | 'stopped';
  let phase: Phase = 'idle';
  let tickIntervalId: number | null = null;
  let runStartMs = 0;
  let timeoutId: number | null = null;
  // Episode counter at the last fit round — drives "every N episodes, fit".
  let episodesAtLastFit = 0;

  // The last step result (for the HUD between ticks).
  type LastResult = {
    epsilon: number;
    qValues: Float64Array;
    gatePBypass: number;
    gateFired: boolean;
    queenFired: boolean;
  };
  let lastResult: LastResult = {
    epsilon: NaN,
    qValues: new Float64Array(spec.nActions),
    gatePBypass: NaN,
    gateFired: false,
    queenFired: false,
  };

  // Compute the ε schedule. If the user moved the ε sliders, override the
  // default; otherwise use the fqi.ts default (which matches the validated
  // prototype).
  function currentEpsilon(): number {
    if (epsScheduleOverride) return epsScheduleOverride(agent.roundCount);
    return epsilonForRound(agent.roundCount);
  }

  // ── Panel ───────────────────────────────────────────────────────────
  const panel = new SidePanel({
    onBrush: (b) => { brush = b; },
    onClearWorld: () => {
      if (phase === 'running') return;
      generateEmpty(world);
    },
    onGenerate: (kind) => {
      if (phase === 'running') return;
      if (kind === 'spiral') generateSpiral(world);
      else if (kind === 'random') generateRandom(world, worldRng);
      else if (kind === 'cluster') generateClustered(world, worldRng);
      else generateEmpty(world);
    },
    onRandomize: () => {
      if (phase === 'running') return;
      randomizeWorld(world, worldRng);
    },
    onGamma: (v) => {
      gamma = v;
      agent.q.gamma = v;
    },
    onLambda: (v) => {
      lambda = v;
      agent.q.lambda = v;
    },
    onEpsPerRoundMax: (v) => {
      epsMax = v;
      epsScheduleOverride = (round: number) =>
        round === 0 ? 1.0 : Math.max(epsMin, epsMax - 0.07 * round);
    },
    onEpsPerRoundMin: (v) => {
      epsMin = v;
      epsScheduleOverride = (round: number) =>
        round === 0 ? 1.0 : Math.max(epsMin, epsMax - 0.07 * round);
    },
    onSpeed: (hz) => {
      speedHz = hz;
      if (phase === 'running') restartTickTimer();
    },
    onTimeout: (ms) => {
      timeoutMs = ms;
      // If running, re-arm the timeout with the new deadline relative to the
      // elapsed time. If the new timeout is already past, stop now.
      if (phase === 'running') armTimeout();
    },
    onSensorP: (p) => { sensorP = p; env.sensorP = p; },
    // Reward weights — live. Mutate the shared env.rewards object's fields
    // by reference; the next step reads them.
    onRewardGold: (v) => { env.rewards.gold = v; },
    onRewardBump: (v) => { env.rewards.bumpBase = v; },
    onRewardCoverage: (v) => { env.rewards.coverage = v; },
    onRewardStep: (v) => { env.rewards.step = v; },
    onReveal: (b) => { revealAll = b; },
    onShowBelief: (b) => { showBelief = b; },
    onFitNow: () => {
      if (phase !== 'running') return;
      const stats = agent.fitRound();
      if (stats) {
        agent.epsilon = currentEpsilon();
        episodesAtLastFit = agent.episodes;
      }
      // Also try to fit the gate — usually a no-op until enough labels
      // have accumulated, but the manual button is a good "force it now".
      agent.fitGate();
      pushPanelUpdate();
    },
    onStart: () => startRun(),
    onStop: () => stopRun(),
    onReset: () => resetRun(),
    onGateToggle: (enabled) => { agent.gateEnabled = enabled; pushPanelUpdate(); },
    onSelectModel: (kind) => {
      if (kind === modelKind) return;
      // Hard reset: stop the run, discard Q/replay/gate, build a fresh
      // Agent of the new variant. Mirrors the active-perception precedent.
      if (phase === 'running') stopRun();
      modelKind = kind;
      spec = specFor(kind);
      agent = new Agent({ seed: (Math.random() * 1e9) | 0, lambda, gamma, spec });
      agentAnim = makeAgentAnim(START_R, START_C);
      lastResult = {
        epsilon: NaN,
        qValues: new Float64Array(spec.nActions),
        gatePBypass: NaN,
        gateFired: false,
        queenFired: false,
      };
      // Reflect in URL without reloading.
      const u = new URL(window.location.href);
      u.searchParams.set('model', kind);
      window.history.replaceState({}, '', u.toString());
      pushPanelUpdate();
    },
    onSelectWorld: (kind) => {
      if (kind === worldType) return;
      worldType = kind;
      const u = new URL(window.location.href);
      if (kind === 'auto') u.searchParams.delete('world');
      else u.searchParams.set('world', kind);
      window.history.replaceState({}, '', u.toString());
      pushPanelUpdate();
    },
  });
  panelSlot.appendChild(panel.el);

  // ── Painting input ──────────────────────────────────────────────────
  let painting = false;
  let lastPaintCell: { r: number; c: number } | null = null;

  function eventToCell(e: PointerEvent | MouseEvent): { r: number; c: number } | null {
    const rect = lakeCanvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const w = rect.width;
    const h = rect.height;
    const cell = Math.min(w / GRID, h / GRID);
    const ox = (w - cell * GRID) / 2;
    const oy = (h - cell * GRID) / 2;
    const c = Math.floor((x - ox) / cell);
    const r = Math.floor((y - oy) / cell);
    if (r < 0 || r >= GRID || c < 0 || c >= GRID) return null;
    return { r, c };
  }

  function paintAt(r: number, c: number): void {
    if (phase !== 'idle' && r === env.r && c === env.c) return;
    const kind = brush === 'gold' ? 1 : brush === 'stone' ? 2 : 0;
    world.paint(r, c, kind);
    if (phase !== 'idle') {
      // Mid-run paint: keep env.goldLeft honest. (We no longer snapshot the
      // world — each episode randomises — so user edits only live until the
      // next episode boundary.)
      env.resyncGoldLeft();
    }
  }

  function paintLine(a: { r: number; c: number }, b: { r: number; c: number }): void {
    let dx = Math.abs(b.c - a.c);
    let dy = -Math.abs(b.r - a.r);
    const sx = a.c < b.c ? 1 : -1;
    const sy = a.r < b.r ? 1 : -1;
    let err = dx + dy;
    let cr = a.r;
    let cc = a.c;
    while (true) {
      paintAt(cr, cc);
      if (cr === b.r && cc === b.c) break;
      const e2 = 2 * err;
      if (e2 >= dy) { err += dy; cc += sx; }
      if (e2 <= dx) { err += dx; cr += sy; }
    }
  }

  lakeCanvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    lakeCanvas.setPointerCapture(e.pointerId);
    painting = true;
    const cell = eventToCell(e);
    if (cell) {
      paintAt(cell.r, cell.c);
      lastPaintCell = cell;
    }
  });
  lakeCanvas.addEventListener('pointermove', (e) => {
    if (!painting) return;
    const cell = eventToCell(e);
    if (cell) {
      if (lastPaintCell) paintLine(lastPaintCell, cell);
      else paintAt(cell.r, cell.c);
      lastPaintCell = cell;
    }
  });
  const endPaint = (e: PointerEvent) => {
    painting = false;
    lastPaintCell = null;
    try { lakeCanvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  lakeCanvas.addEventListener('pointerup', endPaint);
  lakeCanvas.addEventListener('pointercancel', endPaint);
  lakeCanvas.addEventListener('pointerleave', () => { lastPaintCell = null; });

  // ── Render loop ─────────────────────────────────────────────────────
  const ro = new ResizeObserver(() => renderer.resize());
  ro.observe(lakeCanvas);

  requestAnimationFrame(function frame(t) {
    agentAnim.r = env.r;
    agentAnim.c = env.c;
    const state = {
      world,
      env,
      agent: agentAnim,
      goal: null as { r: number; c: number } | null,
      revealAll,
      showBelief,
      gateFiring: lastResult.gateFired && agent.gateEnabled,
      queenFiring: lastResult.queenFired,
    };
    renderer.frame(t, state);
    requestAnimationFrame(frame);
  });

  // ── Tick timer ──────────────────────────────────────────────────────
  function restartTickTimer(): void {
    if (tickIntervalId !== null) window.clearInterval(tickIntervalId);
    const periodMs = 1000 / Math.max(1, speedHz);
    tickIntervalId = window.setInterval(tick, periodMs);
  }

  /** Arm (or re-arm) the run timeout based on the current `timeoutMs` and
   *  the already-elapsed time. If the new deadline is already past, stop. */
  function armTimeout(): void {
    if (timeoutId !== null) window.clearTimeout(timeoutId);
    const elapsed = performance.now() - runStartMs;
    const remaining = timeoutMs - elapsed;
    if (remaining <= 0) {
      stopRun();
      return;
    }
    timeoutId = window.setTimeout(() => {
      if (phase === 'running') stopRun();
    }, remaining);
  }

  function tick(): void {
    if (phase !== 'running') return;
    if (env.done) {
      // Episode finished. Start the next one (on a fresh random layout) and
      // maybe run a fit round.
      startEpisode();
    }
    // Keep the agent's ε in sync with the (possibly overridden) schedule.
    agent.epsilon = currentEpsilon();
    const res = agent.act(env);
    if (res.event === 'bump') agentAnim.bumpFlash = 1.0;
    const { dir } = decodeAction(res.action);
    const [dr, dc] = DIRECTIONS[dir];
    if (dr !== 0 || dc !== 0) {
      agentAnim.targetHeading = Math.atan2(dr, dc);
    }
    lastResult = {
      epsilon: res.epsilon,
      qValues: res.qValues,
      gatePBypass: res.gatePBypass,
      gateFired: res.gateFired,
      queenFired: res.queenFired,
    };
    pushPanelUpdate();
  }

  /** Start the next episode within a run. Generates a FRESH random layout
   *  (so the agent can't memorise a fixed map), resets the env, tells the
   *  agent an episode boundary happened. Also fires a fit round every
   *  EPISODES_PER_FIT_ROUND episodes so Q advances across the run. */
  function startEpisode(): void {
    // Maybe run a fit round.
    if (agent.episodes - episodesAtLastFit >= EPISODES_PER_FIT_ROUND) {
      const stats = agent.fitRound();
      if (stats) {
        agent.epsilon = currentEpsilon();
        episodesAtLastFit = agent.episodes;
      }
      // Same cadence: maybe refit the gate on newly-collected labels.
      agent.fitGate();
    }
    // Pick the layout. Default ('auto') = the 60/20/20 mix from
    // randomizeWorld (clustered/spiral/scatter). When the user freezes a
    // world type via the sidebar or ?world= URL param, that type is used
    // every episode — useful for inspecting how a variant behaves on a
    // single layout.
    pickWorld();
    world.paint(START_R, START_C, FREE);
    env.reset(START_R, START_C);
    agent.startEpisode();
    agentAnim.r = START_R;
    agentAnim.c = START_C;
    agentAnim.sr = START_R;
    agentAnim.sc = START_C;
  }

  /** Pick the next episode's world according to the user's `worldType` setting. */
  function pickWorld(): void {
    switch (worldType) {
      case 'cluster':
        generateClustered(world, worldRng);
        break;
      case 'spiral': {
        const g = GRID;
        const lo = 5;
        const hi = g - 6;
        const cx = lo + Math.floor(worldRng() * (hi - lo + 1));
        const cy = lo + Math.floor(worldRng() * (hi - lo + 1));
        generateSpiral(world, { cx, cy, rng: worldRng });
        break;
      }
      case 'random':
        generateRandom(world, worldRng);
        break;
      case 'empty':
        generateEmpty(world);
        break;
      case 'auto':
      default:
        randomizeWorld(world, worldRng);
        break;
    }
  }

  function pushPanelUpdate(): void {
    const elapsed = phase === 'running' ? performance.now() - runStartMs : 0;
    const episodeGoldTotal = agent.episodeGoldEaten + env.goldLeft;
    panel.update({
      steps: agent.totalStepsRead,
      episodes: agent.episodes,
      episodeGoldEaten: agent.episodeGoldEaten,
      episodeGoldTotal,
      episodeReturn: agent.episodeReturn,
      lastEpisodeGold: agent.lastEpisodeGold,
      lastEpisodeReturn: agent.lastEpisodeReturn,
      goldEaten: agent.goldEaten,
      goldLeft: env.goldLeft,
      returnG: agent.returnG,
      bumps: agent.bumps,
      roundCount: agent.roundCount,
      epsilon: agent.epsilon,
      replaySize: agent.replay.size,
      replayCapacity: agent.replay.capacity,
      lastFitLoss: agent.lastFit ? agent.lastFit.fitLoss : -1,
      running: phase === 'running',
      elapsedMs: elapsed,
      timeoutMs,
      qValues: lastResult.qValues,
      // Gate state.
      gateEnabled: agent.gateEnabled,
      gateFitted: agent.gate.fitted,
      gatePBypass: lastResult.gatePBypass,
      gateFired: lastResult.gateFired,
      queenFired: lastResult.queenFired,
      gateFiredCount: agent.gateFiredCount,
      gateEpisodeSteps: env.steps,
      gateSampleCount: agent.gateLabels.size,
      gateSampleCapacity: agent.gateLabels.capacity,
      gateTrainAcc: agent.lastGateFit ? agent.lastGateFit.trainAcc : -1,
      gatePositiveRate: agent.gateLabels.positiveRate,
      modelKind,
      useKnight: spec.useKnight,
      useQueen: spec.useQueen,
      worldType,
    });
  }

  // ── Lifecycle ───────────────────────────────────────────────────────
  function startRun(): void {
    if (phase === 'running') return;
    agent = new Agent({ seed: (Math.random() * 1e9) | 0, lambda, gamma, spec });
    agent.epsilon = currentEpsilon();
    agentAnim = makeAgentAnim(START_R, START_C);
    // Resize the idle-HUD fallback buffer to match the active variant.
    lastResult = {
      epsilon: NaN,
      qValues: new Float64Array(spec.nActions),
      gatePBypass: NaN,
      gateFired: false,
      queenFired: false,
    };
    env.maxSteps = EPISODE_STEP_CAP;
    // First episode uses whatever the user painted (or the default spiral
    // at startup). After that, startEpisode() randomises.
    world.paint(START_R, START_C, FREE);
    env.reset(START_R, START_C);
    env.sensorP = sensorP;
    agent.startRun();
    episodesAtLastFit = 0;
    phase = 'running';
    runStartMs = performance.now();
    panel.resetCharts();
    restartTickTimer();
    armTimeout();
  }

  function stopRun(): void {
    if (phase !== 'running') return;
    phase = 'stopped';
    if (tickIntervalId !== null) {
      window.clearInterval(tickIntervalId);
      tickIntervalId = null;
    }
    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
    pushPanelUpdate();
  }

  function resetRun(): void {
    stopRun();
    phase = 'idle';
    env.reset(START_R, START_C);
    agentAnim = makeAgentAnim(START_R, START_C);
    lastResult = { epsilon: NaN, qValues: new Float64Array(spec.nActions), gatePBypass: NaN, gateFired: false, queenFired: false };
    panel.resetCharts();
    pushPanelUpdate();
  }

  // Reset env once at startup so the belief window is populated for rendering.
  env.reset(START_R, START_C);
  pushPanelUpdate();
}

main();
